/**
 * عروضي — template inputs <-> the nullable `template` JSON column on
 * venue_overlay_promos. The engine never reads this column; it only holds what
 * the owner typed so editing can reopen the fields (the rendered PNGs are the
 * real media the engine rotates, in media_ids order).
 */
import { MAX_DESIGNS } from './constants';

export const emptyItem = (palette = 'honey') => ({ key: Math.random().toString(36).slice(2, 9), title: '', line: '', price: '', palette, photo: null, mediaId: null, dirty: true });

/** `hasLogo`/`venueName`: a venue without a logo starts in «اسم المقهى» mode; the name defaults to the company name (max 18). */
export function newTemplate({ hasLogo = false, venueName = '' } = {}) {
  return {
    brand: hasLogo ? 'logo' : 'name',
    venueName: String(venueName || '').slice(0, 18),
    currency: 'SAR',
    items: [emptyItem()],
    qr: { type: 'none', input: '', ssid: '', wifiPass: '', linkId: null, display: '', url: '' },
  };
}

/** Stored JSON -> editable state. Wi-Fi password is never stored, so it comes back empty. */
export function templateFromStored(tpl, mediaIds = []) {
  if (!tpl || !Array.isArray(tpl.items) || !tpl.items.length) return null;
  const q = tpl.qr || {};
  return {
    currency: tpl.currency || 'SAR',
    // the logo the designs were last RENDERED with (undefined = a template saved before this was recorded)
    logo: tpl.logo,
    // café identity: 'logo' | 'name'; null = a template saved before this existed (it keeps rendering the old way)
    brand: tpl.brand || null,
    venueName: tpl.venue_name || '',
    items: tpl.items.slice(0, MAX_DESIGNS).map((it, i) => ({
      key: Math.random().toString(36).slice(2, 9),
      title: it.title || '', line: it.line || '', price: it.price || '',
      palette: it.palette || tpl.palette || 'honey', // per-design theme; old templates had one shared palette
      image: it.image || null, // public URL of the rendered PNG (lets the list show the card without the media library)
      photo: it.photo_media_id ? { id: it.photo_media_id } : null,
      mediaId: mediaIds[i] || null,
      dirty: false,
    })),
    qr: { type: q.type || 'none', input: q.display || '', ssid: q.ssid || '', wifiPass: '', linkId: q.link_id || null, display: q.display || '', url: '' },
  };
}

/** Editable state -> the JSON stored on the promo. No secrets: the Wi-Fi password is dropped. */
export function templateToStored(tpl, shape, logoUrl = null) {
  return {
    version: 3,
    shape,
    palette: tpl.items[0]?.palette || 'honey', // default for readers that only know the shared palette
    logo: logoUrl,
    ...(tpl.brand ? { brand: tpl.brand, venue_name: (tpl.venueName || '').trim().slice(0, 18) || null } : {}),
    currency: tpl.currency,
    items: tpl.items.map((it) => ({
      title: it.title.trim(),
      line: it.line.trim() || null,
      price: it.price.trim() || null,
      palette: it.palette,
      image: it.image || null,
      photo_media_id: it.photo?.id || null,
    })),
    qr: tpl.qr.type === 'none' ? { type: 'none' } : {
      type: tpl.qr.type,
      link_id: tpl.qr.linkId || null,
      display: tpl.qr.display || null,
      ...(tpl.qr.type === 'wifi' ? { ssid: tpl.qr.ssid } : {}),
    },
  };
}

export const itemIsBlank = (it) => !it.title.trim();
